import { useEffect, useRef, useState } from 'react'
import { removeAiPerson, saveAiPerson, searchAiPeople, type AiAgentShell, type AiPerson } from '../../api/settingsAiAgent'
import { failed, NoteText, type Note } from './parts'

/**
 * Fila "People & access" asa cum o vede cine NU e administrator de platforma
 * pe Global (adminul de firma, la General): cine poate folosi agentul, pe ce
 * firma, cu cati pasi pe intrebare. Administratorul de platforma, pe Global,
 * vede in locul ei tabelul unit din `PlatformAdminsTab` (aceeasi lista, langa
 * treptele lui Adam si alocarea in USD).
 *
 * "Tokens / day" nu se mai da din ecran (29 octombrie 2026): alocarea in USD
 * il inlocuieste. Coloana ramane in baza; un rand care il are il trimite inapoi
 * neschimbat la salvare, iar unul nou pleaca cu 0 (nelimitat).
 *
 * Cand o firma (sau "orice firma") are oameni aici, numai ei si proprietarul
 * platformei vorbesc cu agentul pe firma aia; lista goala = ca pana acum, pe
 * dreptul de modul - si asa nimeni nu ramane inchis afara fara sa se fi hotarat
 * cineva.
 *
 * La General, randurile de pe "orice firma" se vad, dar nu se ating: ce vine de
 * la Global coboara, si nu se scrie inapoi de jos in sus.
 */
export function PeopleTab({ page, onReload }: { page: AiAgentShell; onReload(): void }) {
  const isCompany = page.scope === 'company'

  return (
    <div className="panel panel-default">
      <div className="panel-heading">
        <strong>People &amp; access</strong>
        <span className="text-muted" style={{ fontSize: 12, marginLeft: 8 }}>
          who may use the agent, on which company, with how many steps per question. While a company has nobody here, everyone with
          the module right can use it.
        </span>
      </div>
      <div className="panel-body">
        {!page.peopleReady ? (
          <p className="text-muted" style={{ fontSize: 13, margin: 0 }}>
            Needs the <code>2026_09_04_100003_ai_investigation_budget_users</code> migration.
          </p>
        ) : (
          <>
            <AddPerson page={page} onAdded={onReload} />

            {page.people.length === 0 ? (
              <p className="text-muted" style={{ fontSize: 13, margin: 0 }}>
                Nobody listed: every company works as before.
              </p>
            ) : (
              <table className="table table-condensed" style={{ fontSize: 12, margin: 0 }}>
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Company</th>
                    <th>Steps per question</th>
                    <th>On</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {page.people.map((person) => (
                    <PersonRow
                      key={person.ID}
                      person={person}
                      inherited={isCompany && person.company_id === 0}
                      scope={page.scope}
                      onRemoved={onReload}
                    />
                  ))}
                </tbody>
              </table>
            )}
            <p className="text-muted" style={{ fontSize: 12, margin: '8px 0 0' }}>
              0 = inherit (the company&apos;s, then the global setting). The platform owner is never limited by this
              list.
            </p>
          </>
        )}
      </div>
    </div>
  )
}

function AddPerson({ page, onAdded }: { page: AiAgentShell; onAdded(): void }) {
  const isCompany = page.scope === 'company'
  const [term, setTerm] = useState('')
  const [found, setFound] = useState<{ id: number; name: string; email: string }[]>([])
  const [open, setOpen] = useState(false)
  const [userId, setUserId] = useState(0)
  const [companyId, setCompanyId] = useState(isCompany ? page.companyId : 0)
  const [steps, setSteps] = useState(0)
  const [note, setNote] = useState<Note | null>(null)
  const timer = useRef<number | null>(null)

  /* Cautarea asteapta o clipa dupa ultima tasta: altfel fiecare litera ar fi o
     cerere, si lista ar clipi inapoi la raspunsuri vechi. */
  useEffect(() => {
    if (timer.current) window.clearTimeout(timer.current)
    if (term.trim() === '') {
      setFound([])
      setOpen(false)
      return
    }

    timer.current = window.setTimeout(() => {
      void searchAiPeople(term)
        .then((r) => {
          setFound(r.people)
          setOpen(r.people.length > 0)
        })
        .catch(() => setFound([]))
    }, 250)

    return () => {
      if (timer.current) window.clearTimeout(timer.current)
    }
  }, [term])

  const add = () => {
    setNote({ text: '…' })
    void saveAiPerson({
      user_id: userId,
      company_id: companyId,
      max_steps: steps,
      /* Jetoanele pe zi nu se mai dau din ecran: alocarea in USD le inlocuieste. */
      daily_token_budget: 0,
      enabled: 1,
      scope: page.scope,
    })
      .then((r) => {
        if (!r.ok) {
          setNote({ text: r.error ?? 'Failed', bad: true })
          return
        }
        setNote({ text: 'Added' })
        setTerm('')
        setUserId(0)
        onAdded()
      })
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  return (
    <div
      style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12, position: 'relative' }}
    >
      <div style={{ position: 'relative', flex: '1 1 220px' }}>
        <label style={{ fontSize: 12, margin: 0 }}>Person</label>
        <input
          type="text"
          className="form-control input-sm"
          placeholder="type a name or e-mail"
          autoComplete="off"
          value={term}
          onChange={(e) => {
            setTerm(e.target.value)
            setUserId(0)
          }}
        />
        {open ? (
          <div
            style={{
              position: 'absolute',
              zIndex: 20,
              background: '#fff',
              border: '1px solid #d5dbe1',
              borderRadius: 6,
              maxHeight: 220,
              overflow: 'auto',
              width: '100%',
              boxShadow: '0 6px 18px rgba(0,0,0,.08)',
            }}
          >
            {found.map((p) => (
              <a
                key={p.id}
                href="#"
                style={{ display: 'block', padding: '5px 10px', fontSize: 13, color: '#1f2933' }}
                onClick={(e) => {
                  e.preventDefault()
                  setTerm(p.name)
                  setUserId(p.id)
                  setOpen(false)
                }}
              >
                {p.name}
                {p.email ? ' · ' + p.email : ''}
              </a>
            ))}
          </div>
        ) : null}
      </div>

      <div style={{ flex: '1 1 200px' }}>
        <label style={{ fontSize: 12, margin: 0 }}>Company</label>
        {isCompany ? (
          <input type="text" className="form-control input-sm" value={page.companyName} disabled />
        ) : (
          <select
            className="form-control input-sm"
            value={companyId}
            onChange={(e) => setCompanyId(Number(e.target.value))}
          >
            <option value={0}>Any company</option>
            {page.allCompanies.map((c) => (
              <option key={c.ID} value={c.ID}>
                {c.name}
              </option>
            ))}
          </select>
        )}
      </div>

      <div style={{ width: 130 }}>
        <label style={{ fontSize: 12, margin: 0 }}>Steps per question</label>
        <input
          type="number"
          min={0}
          max={2048}
          className="form-control input-sm"
          placeholder="inherit"
          value={steps}
          onChange={(e) => setSteps(Number(e.target.value))}
        />
      </div>
      <button type="button" className="btn btn-primary btn-sm" onClick={add}>
        Add
      </button>
      <NoteText note={note} />
    </div>
  )
}

function PersonRow({
  person,
  inherited,
  scope,
  onRemoved,
}: {
  person: AiPerson
  inherited: boolean
  scope: string
  onRemoved(): void
}) {
  const [steps, setSteps] = useState(person.max_steps)
  const [on, setOn] = useState(person.enabled)
  const [note, setNote] = useState<Note | null>(null)

  const save = () => {
    setNote({ text: '…' })
    void saveAiPerson({
      user_id: person.user_id,
      company_id: person.company_id,
      max_steps: steps,
      /* Nu se mai da din ecran: pleaca inapoi cum era, ca o salvare sa nu-l stearga. */
      daily_token_budget: person.daily_token_budget,
      enabled: on ? 1 : 0,
      scope,
    })
      .then((r) => setNote(r.ok ? { text: 'Saved' } : { text: r.error ?? 'Failed', bad: true }))
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  const remove = () => {
    if (!window.confirm('Remove this person from the list?')) return

    void removeAiPerson(person.ID)
      .then((r) => (r.ok ? onRemoved() : setNote({ text: 'Could not remove', bad: true })))
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  return (
    <tr style={inherited ? { color: '#6b7480' } : undefined}>
      <td>
        {person.name || '#' + person.user_id}
        <div className="text-muted" style={{ fontSize: 11 }}>
          {person.email}
        </div>
      </td>
      <td>
        {person.company_id > 0 ? (
          person.company || '#' + person.company_id
        ) : (
          <em>{inherited ? 'any company (from Global)' : 'any'}</em>
        )}
      </td>
      <td>
        <input
          type="number"
          min={0}
          max={2048}
          className="form-control input-sm"
          style={{ width: 90 }}
          placeholder="inherit"
          value={steps}
          disabled={inherited}
          onChange={(e) => setSteps(Number(e.target.value))}
        />
      </td>
      <td>
        <input type="checkbox" checked={on} disabled={inherited} onChange={(e) => setOn(e.target.checked)} />
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>
        {!inherited ? (
          <>
            <button type="button" className="btn btn-default btn-xs" onClick={save}>
              Save
            </button>{' '}
            <button type="button" className="btn btn-default btn-xs" onClick={remove}>
              Remove
            </button>{' '}
          </>
        ) : null}
        <NoteText note={note} />
      </td>
    </tr>
  )
}
